import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Container as BaseContainer, PrimaryButton } from './GlobalStyle';
import { useI18n } from '../i18n';
import chileSur from '../assets/welcome/chile-sur.webp';
import bridgeTrain from '../assets/welcome/bridge-train.webp';
import hecrasShot from '../assets/welcome/hecras-shot.webp';
import micuencaShot from '../assets/welcome/micuenca-shot.webp';
import amazonImg from '../assets/welcome/amazon.webp';

const Section = styled.section`
  position: relative;
  height: 100vh;
  height: 100svh;
  width: 100%;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.brandDark};
`;

const Slide = styled(motion.div)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const BackgroundImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  z-index: 0;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 10, 26, 0.85) 0%, rgba(0, 10, 26, 0.35) 45%, transparent 100%);
  z-index: 1;
`;

const ContentContainer = styled(BaseContainer)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 4rem 2rem;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 3rem 1.5rem;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 2.5rem 1.25rem;
  }
`;

const Title = styled.h2`
  color: #fff;
  font-size: clamp(2.2rem, 1.6rem + 2.5vw, 3rem);
  font-weight: 900;
  margin: 0;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
  max-width: 800px;
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.92);
  font-size: clamp(1rem, 0.9rem + 0.5vw, 1.2rem);
  line-height: 1.6;
  max-width: 600px;
  margin: 0;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.35);
  a {
    color: #fff;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const ActionButton = styled(PrimaryButton)`
  padding: 0.8rem 1.5rem;
  font-size: 1.05rem;
`;

const Dots = styled.div`
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  display: flex;
  gap: 0.4rem;
  z-index: 20;
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    bottom: 1.25rem;
    right: 1.25rem;
  }
`;

const Dot = styled.button`
  /* Generous hit area around a small visual dot */
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid #fff;
    background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
    opacity: ${({ $active }) => ($active ? 1 : 0.75)};
    transition: opacity 0.2s ease, transform 0.2s ease, background 0.2s ease;
  }
  &:hover::before {
    opacity: 1;
    transform: scale(1.15);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.5);
  }
`;

const SWIPE_THRESHOLD_PX = 50;

const WelcomeCarousel = () => {
  const { t } = useI18n();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const linkifyMicuenca = (text) => {
    const parts = String(text).split(/(MiCuenca\.cl)/);
    return parts.map((part, i) =>
      part === 'MiCuenca.cl'
        ? <a key={i} href="https://micuenca.cl" target="_blank" rel="noopener noreferrer">MiCuenca.cl</a>
        : part
    );
  };
  const slides = [
    {
      title: t('home.welcome.slide0.title'),
      text: t('home.welcome.slide0.text'),
      to: null,
      image: amazonImg
    },
    {
      title: t('home.welcome.slide1.title'),
      text: t('home.welcome.slide1.text'),
      to: '/solutions',
      image: hecrasShot
    },
    {
      title: t('home.welcome.slide2.title'),
      text: linkifyMicuenca(t('home.welcome.slide2.text')),
      to: '/updates',
      image: micuencaShot
    },
    {
      title: t('home.welcome.slide3.title'),
      text: t('home.welcome.slide3.text'),
      to: '/about',
      image: chileSur
    },
    {
      title: t('home.welcome.slide4.title'),
      text: t('home.welcome.slide4.text'),
      to: '/solutions',
      image: bridgeTrain
    }
  ];
  const altTitlesEn = [
    'Endure. Optimize. Excel.',
    'Automated Hydrology & Hydraulic Modeling',
    'National-Scale Watershed Intelligence',
    'Flood Resilience & Risk Quantification',
    'Critical Infrastructure Protection'
  ];

  const [timerKey, setTimerKey] = useState(0);
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), 10000);
    return () => clearInterval(id);
  }, [timerKey, paused, slides.length]);

  const goTo = (i) => {
    setIndex(((i % slides.length) + slides.length) % slides.length);
    setTimerKey((k) => k + 1);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    goTo(delta < 0 ? index + 1 : index - 1);
  };

  return (
    <Section
      aria-roledescription="carousel"
      aria-label="Highlights"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <Helmet>
        <title>{t('seo.homeTitle')}</title>
        <meta name="description" content={t('seo.homeDescription')} />
        <link rel="preload" as="image" href={slides[0].image} />
      </Helmet>
      <AnimatePresence initial={false}>
        <Slide
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
        >
          <BackgroundImage
            src={slides[index].image}
            alt={altTitlesEn[index]}
          />
          <Overlay />
          <ContentContainer>
            <Title>{slides[index].title}</Title>
            {(Array.isArray(slides[index].text) || slides[index].text) && (
              <Description>{slides[index].text}</Description>
            )}
            {slides[index].to && (
              <ActionButton to={slides[index].to}>{t('home.actionReadUpdate')}</ActionButton>
            )}
          </ContentContainer>
        </Slide>
      </AnimatePresence>
      <Dots role="group" aria-label="Slides">
        {slides.map((_, i) => (
          <Dot
            key={i}
            type="button"
            $active={i === index}
            aria-current={i === index}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </Dots>
    </Section>
  );
};

export default WelcomeCarousel;
